import { Head, Link, router, usePage } from '@inertiajs/react'
import { useState, useEffect } from 'react'
import { Button } from '../../components/ui/button'
import { Card, CardContent } from '../../components/ui/card'
import { Alert, AlertDescription } from '../../components/ui/alert'
import { Badge } from '../../components/ui/badge'
import { Eraser, Send, CheckCircle, XCircle } from 'lucide-react'

const GRID_SIZE = 8
const DEFAULT_COLOR = '#000000' // Default to black

// 10 colors: Black, White, and 8 solid colors (second from each gradient)
const COLORS = [
  // Row 1: Black and White only
  '#000000', '#ffffff',
  // Solid colors (second color from each gradient - optimized for LED display)
  '#ef4444', // Red
  '#f97316', // Orange
  '#facc15', // Yellow
  '#22c55e', // Green
  '#06b6d4', // Cyan
  '#2563eb', // Blue
  '#a855f7', // Purple
  '#ec4899', // Pink
]

export default function Index() {
  const { flash, total_grams, random_gram, random_gram_created_at } = usePage().props

  // Parse URL parameters for prefilled colors
  // Usage: ?colors=ff0000,00ff00,0000ff (hex codes without # prefix)
  const getInitialPixels = () => {
    const params = new URLSearchParams(window.location.search)
    const colorsParam = params.get('colors')

    if (colorsParam) {
      try {
        // Split the colors by comma and normalize them
        const colors = colorsParam.split(',').map(c => {
          // Clean and ensure hex code starts with #
          let color = c.trim().replace(/^#/, '') // Remove # if present

          // Validate hex color format (3 or 6 characters)
          if (/^[0-9A-Fa-f]{3}$|^[0-9A-Fa-f]{6}$/.test(color)) {
            return `#${color}`
          }

          // Invalid color, return black as fallback
          return '#000000'
        })

        // Create grid from colors (fill row by row)
        const grid = []
        let colorIndex = 0

        for (let row = 0; row < GRID_SIZE; row++) {
          const rowColors = []
          for (let col = 0; col < GRID_SIZE; col++) {
            rowColors.push(colors[colorIndex] || '#000000')
            colorIndex++
          }
          grid.push(rowColors)
        }

        return grid
      } catch (error) {
        console.error('Error parsing colors from URL:', error)
        return Array(GRID_SIZE).fill(null).map(() => Array(GRID_SIZE).fill('#000000'))
      }
    }

    return Array(GRID_SIZE).fill(null).map(() => Array(GRID_SIZE).fill('#000000'))
  }

  const initialPixels = getInitialPixels()
  const defaultPixels = Array(GRID_SIZE).fill(null).map(() => Array(GRID_SIZE).fill('#000000'))
  const [pixels, setPixels] = useState(initialPixels)
  const [lastSentPixels, setLastSentPixels] = useState(defaultPixels)
  const [selectedColor, setSelectedColor] = useState(DEFAULT_COLOR)
  const [isDrawing, setIsDrawing] = useState(false)
  const [showFlash, setShowFlash] = useState(false)
  const [elapsedTime, setElapsedTime] = useState(0)

  useEffect(() => {
    if (flash?.success || flash?.error) {
      setShowFlash(true)
      const timer = setTimeout(() => setShowFlash(false), 5000)
      return () => clearTimeout(timer)
    }
  }, [flash])

  // Update lastSentPixels when successfully sent
  useEffect(() => {
    if (flash?.success) {
      setLastSentPixels(pixels)
    }
  }, [flash?.success])

  // Poll for updates every 5 seconds
  useEffect(() => {
    const interval = setInterval(() => {
      router.reload({ only: ['random_gram', 'random_gram_created_at', 'total_grams'] })
    }, 5000)

    return () => clearInterval(interval)
  }, [])

  // Update elapsed time every second based on database created_at timestamp
  useEffect(() => {
    if (!random_gram_created_at) return

    const updateElapsedTime = () => {
      const createdAt = new Date(random_gram_created_at).getTime()
      setElapsedTime(Date.now() - createdAt)
    }

    // Update immediately
    updateElapsedTime()

    // Then update every second
    const interval = setInterval(updateElapsedTime, 1000)

    return () => clearInterval(interval)
  }, [random_gram_created_at])

  // Format elapsed time as seconds/minutes/hours
  const formatElapsedTime = (milliseconds) => {
    const seconds = Math.floor(milliseconds / 1000)
    if (seconds < 60) {
      return `${seconds} second${seconds !== 1 ? 's' : ''}`
    }
    const minutes = Math.floor(seconds / 60)
    if (minutes < 60) {
      return `${minutes} minute${minutes !== 1 ? 's' : ''}`
    }
    const hours = Math.floor(minutes / 60)
    const remainingMinutes = minutes % 60
    if (remainingMinutes > 0) {
      return `${hours} hour${hours !== 1 ? 's' : ''}, ${remainingMinutes} minute${remainingMinutes !== 1 ? 's' : ''}`
    }
    return `${hours} hour${hours !== 1 ? 's' : ''}`
  }

  // Helper function to compare two pixel grids
  const arePixelsEqual = (pixels1, pixels2) => {
    return JSON.stringify(pixels1) === JSON.stringify(pixels2)
  }

  // Check if current pixels differ from last sent pixels
  const hasChanges = !arePixelsEqual(pixels, lastSentPixels)

  const handlePixelClick = (row, col) => {
    const newPixels = pixels.map((r, i) =>
      i === row ? r.map((c, j) => (j === col ? selectedColor : c)) : r
    )
    setPixels(newPixels)
  }

  const handlePixelMouseEnter = (row, col) => {
    if (isDrawing) {
      handlePixelClick(row, col)
    }
  }

  const handleMouseDown = () => {
    setIsDrawing(true)
  }

  const handleMouseUp = () => {
    setIsDrawing(false)
  }

  const handleClear = () => {
    setPixels(Array(GRID_SIZE).fill(null).map(() => Array(GRID_SIZE).fill('#000000')))
  }

  const handleSend = () => {
    router.post('/', {
      pixels: pixels,
    })
  }

  return (
    <>
      <Head title="Pixelgram - Draw & Send" />

      <div className="min-h-screen flex items-center justify-center p-4 bg-neutral-100">
        <div className="w-full max-w-2xl space-y-6">
          {/* Header */}
          <div className="text-center space-y-3">
            <h1 className="text-4xl md:text-5xl font-bold tracking-tight [text-shadow:0_1px_0_white]">
              Send me a <span style={{ color: '#ef4444', fontFamily: '"Pixelify Sans", sans-serif', fontWeight: 500, position: 'relative', top: '-2px' }}>p</span>
              <span style={{ color: '#f97316', fontFamily: '"Pixelify Sans", sans-serif', fontWeight: 500, position: 'relative', top: '-2px' }}>i</span>
              <span style={{ color: '#facc15', fontFamily: '"Pixelify Sans", sans-serif', fontWeight: 500, position: 'relative', top: '-2px' }}>x</span>
              <span style={{ color: '#22c55e', fontFamily: '"Pixelify Sans", sans-serif', fontWeight: 500, position: 'relative', top: '-2px' }}>e</span>
              <span style={{ color: '#06b6d4', fontFamily: '"Pixelify Sans", sans-serif', fontWeight: 500, position: 'relative', top: '-2px' }}>l</span>
              <span style={{ color: '#2563eb', fontFamily: '"Pixelify Sans", sans-serif', fontWeight: 500, position: 'relative', top: '-2px' }}>g</span>
              <span style={{ color: '#a855f7', fontFamily: '"Pixelify Sans", sans-serif', fontWeight: 500, position: 'relative', top: '-2px' }}>r</span>
              <span style={{ color: '#ec4899', fontFamily: '"Pixelify Sans", sans-serif', fontWeight: 500, position: 'relative', top: '-2px' }}>a</span>
              <span style={{ color: '#ef4444', fontFamily: '"Pixelify Sans", sans-serif', fontWeight: 500, position: 'relative', top: '-2px' }}>m</span>!
            </h1>
            <p className="text-sm text-muted-foreground max-w-xl mx-auto">
              Whatever you send will{' '}
              <a
                href="https://x.com/Shpigford/status/1978903733145665859"
                target="_blank"
                rel="noopener noreferrer"
                className="underline hover:no-underline"
              >
                show up
              </a>
              {' '}on an LED screen on my desk. Be kind. :)
            </p>
            <div className="flex items-center justify-center gap-2 pt-1">
              <Link href="/all" className="group">
                <Badge variant="success" className="text-sm px-3 py-1 cursor-pointer transition-all overflow-hidden inline-flex flex-col items-center">
                  <span>{total_grams?.toLocaleString() || 0} pixelgrams sent</span>
                  <span className="max-h-0 group-hover:max-h-6 transition-all duration-300 overflow-hidden block text-[10px] font-normal">
                    proceed with caution :)
                  </span>
                </Badge>
              </Link>
            </div>
          </div>

          {/* Main Card */}
          <Card className="overflow-hidden">
            <CardContent className="p-6 md:p-8 space-y-6">
              {/* Canvas Grid and Color Picker Side by Side */}
              <div className="flex flex-col md:flex-row gap-6 items-start justify-center">
                {/* Canvas Grid */}
                <div className="flex-shrink-0 mx-auto">
                  <div
                    className="inline-block border-4 border-foreground rounded-lg overflow-hidden shadow-lg"
                    onMouseDown={handleMouseDown}
                    onMouseUp={handleMouseUp}
                    onMouseLeave={handleMouseUp}
                  >
                    {pixels.map((row, rowIndex) => (
                      <div key={rowIndex} className="flex">
                        {row.map((color, colIndex) => (
                          <button
                            key={`${rowIndex}-${colIndex}`}
                            className="w-10 h-10 sm:w-12 sm:h-12 md:w-14 md:h-14 border border-neutral-200 hover:opacity-80 transition-opacity active:scale-95"
                            style={{ backgroundColor: color }}
                            onClick={() => handlePixelClick(rowIndex, colIndex)}
                            onMouseEnter={() => handlePixelMouseEnter(rowIndex, colIndex)}
                            type="button"
                          />
                        ))}
                      </div>
                    ))}
                  </div>
                </div>

                {/* Color Picker */}
                <div className="space-y-3 mx-auto">
                  <h3 className="hidden md:block text-sm font-medium text-muted-foreground">
                    Select Color
                  </h3>
                  <div className="flex flex-wrap md:grid md:grid-cols-2 gap-1 justify-center md:justify-start">
                    {COLORS.map((color, index) => (
                      <button
                        key={`${color}-${index}`}
                        className={`w-6 h-6 md:w-10 md:h-10 rounded border transition-all hover:scale-110 active:scale-95 ${
                          selectedColor === color
                            ? 'border-primary ring-2 ring-primary ring-offset-1 scale-105 z-10'
                            : 'border-neutral-300 hover:border-neutral-400'
                        }`}
                        style={{ backgroundColor: color }}
                        onClick={() => setSelectedColor(color)}
                        type="button"
                        aria-label={`Select color ${color}`}
                      />
                    ))}
                  </div>

                  {/* Random Gram Display */}
                  {random_gram && (
                    <div className="pt-4 space-y-2 hidden md:block">
                      <h3 className="text-xs font-medium text-muted-foreground">
                        On my screen now for <span className="tabular-nums">{formatElapsedTime(elapsedTime)}</span>
                      </h3>
                      <div className="inline-block border-2 border-black rounded overflow-hidden">
                        {random_gram.map((row, rowIndex) => (
                          <div key={rowIndex} className="flex">
                            {row.map((color, colIndex) => (
                              <div
                                key={`${rowIndex}-${colIndex}`}
                                className="w-3 h-3 border border-black"
                                style={{ backgroundColor: color }}
                              />
                            ))}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {/* Actions */}
              <div className="w-full space-y-4">
                <Button
                  onClick={handleSend}
                  variant="black"
                  className="w-full transition-all hover:scale-[1.02] hover:shadow-lg active:scale-[0.98]"
                  size="lg"
                  type="button"
                  disabled={!hasChanges}
                >
                  <Send className="mr-2 h-4 w-4" />
                  Send pixelgram
                </Button>

                {/* Flash Message */}
                {showFlash && (flash?.success || flash?.error) && (
                  <Alert variant={flash.success ? "success" : "destructive"} className="shadow-lg">
                    {flash.success ? (
                      <CheckCircle className="h-4 w-4" />
                    ) : (
                      <XCircle className="h-4 w-4" />
                    )}
                    <AlertDescription className="flex items-center justify-between">
                      <span>{flash.success || flash.error}</span>
                      <button
                        onClick={() => setShowFlash(false)}
                        className="ml-4 text-sm underline hover:no-underline"
                      >
                        Dismiss
                      </button>
                    </AlertDescription>
                  </Alert>
                )}
              </div>
            </CardContent>
          </Card>

          {/* Attribution */}
          <div className="text-center">
            <p className="text-sm text-muted-foreground">
              Made by{' '}
              <a
                href="https://x.com/Shpigford"
                target="_blank"
                rel="noopener noreferrer"
                className="font-medium hover:underline"
              >
                @Shpigford
              </a>
            </p>
          </div>
        </div>
      </div>
    </>
  )
}
